/**
 * STOQ (RestockRocket) + theme cart drawer bridge.
 * Validates mixed-cart rules and adds via /cart/add.js so STOQ can attach selling plans.
 */
(function () {
  'use strict';

  var DEFAULT_MIXED_CART_MESSAGE =
    'Preorders must be purchased separately from regular items. Please complete your current order first, or clear your cart to continue.';

  function stoqApi() {
    return window._RestockRocket || null;
  }

  function getSellingPlan(variantId) {
    var rr = stoqApi();
    if (!rr || typeof rr.getSellingPlan !== 'function') return null;
    return rr.getSellingPlan(Number(variantId));
  }

  function planBlocksMixedCart(plan) {
    return !!(plan && plan.allow_mixed_cart === false);
  }

  function mixedCartMessage(plan) {
    if (plan && plan.mixed_cart_error_message) {
      return String(plan.mixed_cart_error_message).trim();
    }
    return DEFAULT_MIXED_CART_MESSAGE;
  }

  function fetchCartItems() {
    if (stoqApi() && typeof stoqApi().getCart === 'function') {
      return stoqApi().getCart().then(function (cart) {
        return cart.items || [];
      });
    }

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        return cart.items || [];
      });
  }

  function getMixedCartConflict(variantId, cartItems) {
    var addingPlan = getSellingPlan(variantId);
    var addingRestricted = planBlocksMixedCart(addingPlan);
    var addingRegular = !addingRestricted;
    var conflictPlan = null;
    var i;

    if (!cartItems || !cartItems.length) return null;

    for (i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      var itemVariantId = item.variant_id || item.id;
      var itemPlan = getSellingPlan(itemVariantId);
      var itemRestricted = planBlocksMixedCart(itemPlan);
      var itemRegular = !itemRestricted;

      if (addingRestricted && itemRegular) {
        conflictPlan = addingPlan || itemPlan;
        break;
      }

      if (addingRegular && itemRestricted) {
        conflictPlan = itemPlan || addingPlan;
        break;
      }
    }

    return conflictPlan;
  }

  function bodyVariantId(body) {
    if (typeof body === 'string') {
      var params = new URLSearchParams(body);
      return parseInt(params.get('id'), 10);
    }

    if (body && typeof body === 'object') {
      if (body.id) return parseInt(body.id, 10);
      if (body.items && body.items[0] && body.items[0].id) return parseInt(body.items[0].id, 10);
    }

    return NaN;
  }

  function serializeRequestBody(body) {
    if (typeof body === 'string') {
      return {
        payload: body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      };
    }

    return {
      payload: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' }
    };
  }

  function clearFormWarning(formEl) {
    if (!formEl) return;
    var existing = formEl.querySelector('.yt-stoq-warning');
    if (existing) existing.remove();
  }

  function ensureAlertModal() {
    var root = document.getElementById('yt-stoq-modal');
    if (root) return root;

    root = document.createElement('div');
    root.id = 'yt-stoq-modal';
    root.className = 'yt-stoq-modal';
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML =
      '<div class="yt-stoq-modal__overlay" data-stoq-modal-close tabindex="-1"></div>'
      + '<div class="yt-stoq-modal__panel" role="alertdialog" aria-modal="true" aria-labelledby="yt-stoq-modal-title">'
      + '<h3 id="yt-stoq-modal-title" class="yt-stoq-modal__title">Unable to add to cart</h3>'
      + '<p class="yt-stoq-modal__message" data-stoq-modal-message></p>'
      + '<button type="button" class="yt-stoq-modal__button global-button global-button--primary" data-stoq-modal-close>OK</button>'
      + '</div>';

    document.body.appendChild(root);

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-stoq-modal-close]')) {
        hideAlertModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.getAttribute('aria-hidden') === 'false') {
        hideAlertModal();
      }
    });

    return root;
  }

  function hideAlertModal() {
    var root = document.getElementById('yt-stoq-modal');
    if (!root) return;
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('yt-stoq-modal-open');
  }

  function showAlertModal(message, title) {
    var root = ensureAlertModal();
    var titleEl = root.querySelector('.yt-stoq-modal__title');
    var messageEl = root.querySelector('[data-stoq-modal-message]');

    if (titleEl) titleEl.textContent = title || 'Unable to add to cart';
    if (messageEl) messageEl.textContent = message;

    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('yt-stoq-modal-open');

    var focusBtn = root.querySelector('[data-stoq-modal-close]');
    if (focusBtn) focusBtn.focus();
  }

  function showFormWarning(formEl, message) {
    clearFormWarning(formEl);
    showAlertModal(message, 'Unable to add to cart');
  }

  function validateMixedCart(variantId, cartItems) {
    var conflict = getMixedCartConflict(variantId, cartItems);
    if (!conflict) return { allowed: true };

    return {
      allowed: false,
      message: mixedCartMessage(conflict)
    };
  }

  function cartCountHint() {
    var badge = document.querySelector('.cart_count');
    if (!badge) return null;
    var n = parseInt(String(badge.textContent || '').replace(/[^\d]/g, ''), 10);
    return isNaN(n) ? null : n;
  }

  function sellingPlanId(variantId) {
    var plan = getSellingPlan(variantId);
    if (plan == null) return null;
    if (typeof plan === 'number' || typeof plan === 'string') return plan;
    if (plan.id != null) return plan.id;
    if (plan.selling_plan_id != null) return plan.selling_plan_id;
    return null;
  }

  function attachSellingPlan(body, variantId) {
    var planId = sellingPlanId(variantId);
    if (planId == null || isNaN(variantId)) return body;

    if (typeof body === 'string') {
      var params = new URLSearchParams(body);
      if (!params.get('selling_plan')) {
        params.set('selling_plan', String(planId));
      }
      return params.toString();
    }

    if (body && typeof body === 'object' && !body.selling_plan) {
      body.selling_plan = planId;
    }

    return body;
  }

  function addToCart(body, options) {
    options = options || {};
    var variantId = bodyVariantId(body);
    body = attachSellingPlan(body, variantId);

    // Empty cart: skip pre-add /cart.js — biggest preorder delay on collection ATC.
    var countHint = cartCountHint();
    var cartItemsPromise = countHint === 0
      ? Promise.resolve([])
      : fetchCartItems();

    return cartItemsPromise
      .then(function (cartItems) {
        if (!isNaN(variantId) && cartItems.length) {
          var validation = validateMixedCart(variantId, cartItems);
          if (!validation.allowed) {
            showAlertModal(validation.message, 'Separate checkout required');
            return Promise.reject({ stoqMixedCart: true, message: validation.message });
          }
        }

        var request = serializeRequestBody(body);
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: request.headers,
          body: request.payload
        }).then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            if (window.YTAnalytics && typeof window.YTAnalytics.trackAddToCart === 'function') {
              window.YTAnalytics.trackAddToCart(data);
            }
            return data;
          });
        });
      })
      .catch(function (err) {
        if (err && (err.stoqMixedCart || err.stoqCancelled)) {
          return Promise.reject(err);
        }
        if (err && err.name === 'CartRequestCancelledError') {
          return Promise.reject({ stoqCancelled: true });
        }
        return Promise.reject(err);
      });
  }

  function openDrawerAfterAdd(addedItem) {
    if (window.YTCartDrawer && typeof window.YTCartDrawer.open === 'function') {
      if (addedItem && addedItem.product_id && typeof window.YTCartDrawer.setLastAddedProductId === 'function') {
        window.YTCartDrawer.setLastAddedProductId(addedItem.product_id);
      }
      return window.YTCartDrawer.open();
    }
    return Promise.resolve();
  }

  function isAjaxCart() {
    if (window.PXUTheme && window.PXUTheme.theme_settings && window.PXUTheme.theme_settings.cart_action === 'ajax') {
      return true;
    }

    return !!document.getElementById('yt-cart-drawer');
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = (form.getAttribute('action') || '').toLowerCase();
    return action.indexOf('/cart/add') !== -1;
  }

  function findAddButton(form, preferred) {
    if (preferred && form.contains(preferred)) return preferred;
    return form.querySelector('[name="add"], button[type="submit"], .add_to_cart, .product-form__submit, .product-card__cta, .hp-product-row__cta, .hp-featured-split__cta');
  }

  function serializeForm(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  function waitForStoqReady(timeoutMs) {
    if (stoqApi() && typeof stoqApi().getSellingPlan === 'function') {
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        document.removeEventListener('stoq:loaded', finish);
        document.removeEventListener('stoq:inventory-data-loaded', finish);
        resolve();
      }

      document.addEventListener('stoq:loaded', finish);
      document.addEventListener('stoq:inventory-data-loaded', finish);
      setTimeout(finish, timeoutMs || 900);
    });
  }

  function submitFormViaAjax(form, button) {
    var addButton = findAddButton(form, button);

    if (addButton) {
      addButton.disabled = true;
      addButton.classList.add('disabled');
    }

    var looksPreorder = !!(addButton && (
      addButton.classList.contains('restock-rocket-preorder-button')
      || /pre-?order/i.test(addButton.textContent || '')
    ));

    // Incognito/cold: wait briefly for STOQ so selling_plan can attach before add.
    var ready = looksPreorder ? waitForStoqReady(900) : Promise.resolve();

    return ready
      .then(function () {
        return addToCart(serializeForm(form), { formEl: form });
      })
      .then(function (addedItem) {
        var giftReady = window.YTGiftOptions && typeof window.YTGiftOptions.afterProductAdd === 'function'
          ? window.YTGiftOptions.afterProductAdd(form, addedItem)
          : Promise.resolve();

        // open() already fetches + renders cart — do not fire cart:refresh (was double work).
        return giftReady.then(function () {
          return openDrawerAfterAdd(addedItem);
        }).then(function () {
          return addedItem;
        });
      })
      .catch(function (err) {
        if (!err || (!err.stoqMixedCart && !err.stoqCancelled)) {
          var msg = (err && err.description) || (err && err.message) || 'Unable to add to cart';
          showFormWarning(form, typeof msg === 'string' ? msg : 'Unable to add to cart');
        }
      })
      .finally(function () {
        if (addButton) {
          addButton.disabled = false;
          addButton.classList.remove('disabled');
        }
      });
  }

  function initAjaxCartInterceptors() {
    if (window.__ytAjaxCartInterceptors) return;
    if (!isAjaxCart()) return;
    window.__ytAjaxCartInterceptors = true;

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!isCartAddForm(form)) return;
      if (document.body.classList.contains('cart')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      submitFormViaAjax(form, event.submitter || null);
    }, true);

    document.addEventListener('click', function (event) {
      var button = event.target.closest(
        '.ajax-submit, button.add_to_cart, button.product-form__submit[name="add"], button.product-card__cta[name="add"], button.hp-product-row__cta[name="add"], button.hp-featured-split__cta[name="add"], .restock-rocket-preorder-button'
      );
      if (!button || button.disabled) return;

      var form = button.closest('form');
      if (!form || !isCartAddForm(form)) return;
      if (document.body.classList.contains('cart')) return;

      if (button.type === 'submit') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      submitFormViaAjax(form, button);
    }, true);
  }

  window.YTAddToCart = {
    add: addToCart,
    submitForm: submitFormViaAjax,
    validateMixedCart: validateMixedCart,
    fetchCartItems: fetchCartItems,
    showFormWarning: showFormWarning,
    showAlertModal: showAlertModal,
    hideAlertModal: hideAlertModal,
    openDrawerAfterAdd: openDrawerAfterAdd,
    defaultMixedCartMessage: DEFAULT_MIXED_CART_MESSAGE,
    isAjaxCart: isAjaxCart
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAjaxCartInterceptors);
  } else {
    initAjaxCartInterceptors();
  }
})();
